import { useEffect, useState } from 'react'
import { logger } from '../../logger'
import { useGoogleAnalytics } from '../../providers/GoogleAnalyticsProvider'
import { formatNumber } from '../../utils/formatNumber'
import useBalances from '../../hooks/useBalances'
import { STAKE_COMMISSION, STAKE_COMMISSION_MEMO } from '../../constants/business'
import { ICToken } from '../../interfaces/common'
import { ReviewData, WaterNeuronInfo } from '../../interfaces/staking'
import { GAEventName } from '../../interfaces/googleAnalytics'
import { WaterNeuron } from '../../clients/WaterNeuron'
import { transferPlugCommission } from '../../utils/transferPlugCommission'

export const useStaking = ({
  icpToken,
  nicpToken,
  wnInfo,
  waterNeuron,
}: {
  icpToken: ICToken
  nicpToken: ICToken
  wnInfo: WaterNeuronInfo
  waterNeuron: WaterNeuron
}) => {
  const { refreshBalance } = useBalances()
  const { fireEvent } = useGoogleAnalytics()

  const [success, setSuccess] = useState<null | boolean>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [stakeMode, setStakeMode] = useState<'stake' | 'unstake'>('stake')
  const [amount, setAmount] = useState('')
  const [reviewData, setReviewData] = useState<ReviewData>()

  useEffect(() => {
    refreshBalance(icpToken)
    refreshBalance(nicpToken)
  }, [])

  const { decimals } = icpToken

  const token = stakeMode === 'stake' ? icpToken : nicpToken

  const exchangeRate = Number(wnInfo.exchange_rate)
  const icpDeposited = Number(wnInfo.total_icp_deposited)
  const minDeposit = Number(wnInfo.minimum_deposit_amount)

  let numericAmount = Number(amount) * 10 ** token.decimals
  let commission = Math.floor(numericAmount * STAKE_COMMISSION)
  let rateMultiplier = 1 - STAKE_COMMISSION

  if (numericAmount - commission <= minDeposit || commission < token.fee!) {
    commission = 0
    rateMultiplier = 1
  } else {
    numericAmount -= commission
  }

  let formattedExchangeRate
  if (stakeMode === 'stake') {
    const rate = exchangeRate * 10 ** -decimals * rateMultiplier
    formattedExchangeRate = `1 ICP = ${formatNumber(rate, 'nICP')}`
  } else {
    const rate = (10 ** decimals / exchangeRate) * rateMultiplier
    formattedExchangeRate = `1 nICP = ${formatNumber(rate, 'ICP')}`
  }

  const wtnReward = formatNumber(
    waterNeuron.computeRewards(icpDeposited, numericAmount) * 10 ** -decimals,
    'WTN'
  )

  const nicpReceiveAmount = formatNumber(
    numericAmount * exchangeRate * 10 ** -(decimals * 2),
    'nICP'
  )

  const onSubmit = async (action: () => Promise<void>) => {
    setIsLoading(true)

    try {
      await action()

      if (commission > 0) {
        transferPlugCommission(commission, token, STAKE_COMMISSION_MEMO)
      }

      setReviewData(undefined)

      setTimeout(() => {
        refreshBalance(icpToken)
        refreshBalance(nicpToken)
        setAmount('')
        setSuccess(null)
      }, 3000)

      setSuccess(true)

      fireEvent(GAEventName.STAKE, {
        direction: stakeMode,
        amount: Number(amount),
      })
    } catch (e) {
      setReviewData(undefined)
      setSuccess(false)

      logger.error(`Failed to ${stakeMode} on Water Neuron`, {
        originalError: e,
      })
    }

    setIsLoading(false)
  }

  return {
    stakeMode,
    setStakeMode,
    token,
    amount,
    setAmount,
    numericAmount,
    exchangeRate: formattedExchangeRate,
    setReviewData,
    isLoading,
    commission,
    success,
    setSuccess,
    reviewData,
    onSubmit,
    nicpReceiveAmount,
    wtnReward,
  }
}
